import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Settings, ChevronUp, ChevronDown } from 'lucide-react';
import { t } from '../translations';
import { useLang } from '../hooks/useLang';

// Stores only non-PII consent preference flags — no personal data, tokens, or credentials.
// localStorage is the GDPR-standard approach for client-side consent tracking.
const CONSENT_KEY = 'koberCookieConsent';

/** Custom hook: manages consent state and persistence. */
function useConsentManager() {
  const [visible, setVisible] = useState(false);
  const [prefs, setPrefs]     = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // CONSENT_KEY is a module constant; localStorage and setVisible are stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConsent = useCallback((analytics, marketing) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
    }));
    setVisible(false);
    // CONSENT_KEY is a module constant; localStorage and setVisible are stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { visible, prefs, setPrefs, saveConsent };
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-[#D4A017]' : 'bg-white/15'
      } ${disabled ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function PrefRow({ title, desc, checked, onChange, alwaysOn, alwaysLabel }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5 border-b border-white/6 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white flex items-center gap-2 flex-wrap">
          {title}
          {alwaysOn && (
            <span className="text-xs font-normal text-[#D4A017]/60">{alwaysLabel}</span>
          )}
        </p>
        <p className="text-xs text-white/40 mt-0.5 leading-relaxed">{desc}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={alwaysOn} />
    </div>
  );
}

export default function CookieBanner() {
  const [lang]               = useLang();
  const [expanded, setExpanded] = useState(false);
  const { visible, prefs, setPrefs, saveConsent } = useConsentManager();

  const text       = t[lang].cookieBanner;
  const alwaysLabel = lang === 'RO' ? 'Întotdeauna activ' : 'Always active';

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] cookie-banner-enter"
      style={{
        background:           'rgba(5,5,5,0.97)',
        backdropFilter:       'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop:            '1px solid rgba(212,160,23,0.22)',
        boxShadow:            '0 -4px 32px rgba(0,0,0,0.5)',
      }}
    >
      {/* Expandable preferences panel */}
      {expanded && (
        <div className="border-b border-white/8" style={{ background: 'rgba(12,12,12,0.8)' }}>
          <div className="container mx-auto px-6 lg:px-12 max-w-7xl py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-8">
              <PrefRow
                title={text.necessary.title}
                desc={text.necessary.desc}
                checked={true}
                onChange={() => {}}
                alwaysOn
                alwaysLabel={alwaysLabel}
              />
              <PrefRow
                title={text.analytics.title}
                desc={text.analytics.desc}
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <PrefRow
                title={text.marketing.title}
                desc={text.marketing.desc}
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main bar */}
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl py-3.5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">

          {/* Icon + description */}
          <div className="flex items-start gap-2.5 flex-1 min-w-0">
            <Shield size={15} className="text-[#D4A017] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-white/55 leading-relaxed">
              <span className="text-white font-medium">{text.title}. </span>
              {text.body}{' '}
              <Link
                to="/cookies"
                className="text-[#D4A017]/75 hover:text-[#D4A017] underline underline-offset-2 transition-colors duration-200"
              >
                {text.policyLink}
              </Link>
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <button
              data-testid="cookie-manage-btn"
              onClick={() => setExpanded((e) => !e)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-white/45 hover:text-white/80 border border-white/12 hover:border-white/30 transition-all duration-200"
            >
              <Settings size={11} />
              {text.manage}
              {expanded ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
            </button>

            {expanded && (
              <button
                data-testid="cookie-save-btn"
                onClick={() => saveConsent(prefs.analytics, prefs.marketing)}
                className="px-3.5 py-2 text-xs font-medium border border-[#D4A017]/40 text-[#D4A017] hover:border-[#D4A017]/70 transition-colors duration-200"
              >
                {text.save}
              </button>
            )}

            <button
              data-testid="cookie-reject-btn"
              onClick={() => saveConsent(false, false)}
              className="px-3.5 py-2 text-xs font-medium text-white/45 hover:text-white/80 border border-white/12 hover:border-white/30 transition-all duration-200"
            >
              {text.rejectAll}
            </button>

            <button
              data-testid="cookie-accept-btn"
              onClick={() => saveConsent(true, true)}
              className="px-5 py-2 text-xs font-semibold bg-[#D4A017] hover:bg-[#F5B814] text-black transition-colors duration-200"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {text.acceptAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
